import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, CalendarIcon, ChartBarIcon, ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import managementService from '../api/managementService';
import { renderTelemetryChart, TelemetryChart } from '@myio/energy-chart-sdk';
import type { TelemetryChartConfig } from '@myio/energy-chart-sdk';

interface DeviceDataViewProps { }

export default function DeviceDataView({ }: DeviceDataViewProps) {
  // const { } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<TelemetryChart | null>(null);
  const isDrilldownInProgress = useRef<boolean>(false);

  // Date range state - default to last 30 days
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });

  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Time state for hourly precision
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');

  const [granularity, setGranularity] = useState<'1d' | '1h'>('1d');

  // Helper function to get the full datetime string for v2 API calls
  const getStartDateTime = () => {
    return granularity === '1h' ? `${startDate}T${startTime}:00` : startDate;
  };

  const getEndDateTime = () => {
    return granularity === '1h' ? `${endDate}T${endTime}:00` : endDate;
  };
  // Removed unused showRefreshInfo state

  // Fetch device details
  const { data: device, isLoading, error } = useQuery({
    queryKey: ['device', id],
    queryFn: () => managementService.device.getById(id!),
    enabled: !!id,
  });

  // Create refresh job mutation
  const createRefreshJobMutation = useMutation({
    mutationFn: (data: { startDate: string; endDate: string }) =>
      managementService.aggregateRefresh.createJob({
        startDate: data.startDate,
        endDate: data.endDate,
        batchSizeDays: 1,
        delaySeconds: 2
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refresh-jobs'] });
      alert('Refresh job created successfully! Historical data will be processed in the background.');
    },
    onError: (error: any) => {
      console.error('Failed to create refresh job:', error);
      alert(`Failed to create refresh job: ${error.response?.data?.error || error.message}`);
    },
  });

  // Initialize chart when device data is available
  useEffect(() => {
    if (!device || !chartContainerRef.current) return;

    // Clean up existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    // Initialize new chart with v2 Telemetry API
    const config: TelemetryChartConfig = {
      version: 'v2',
      clientId: 'ADMIN_DASHBOARD_CLIENT',
      clientSecret: 'admin_dashboard_secret_2025',
      deviceId: device.id, // Use device.id instead of gatewayId/slaveId
      readingType: 'energy',
      startDate,
      endDate,
      granularity,
      theme: 'light',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
      iframeBaseUrl: import.meta.env.VITE_IFRAME_URL || window.location.origin,
      apiBaseUrl: import.meta.env.VITE_API_URL || 'http://localhost:3000',
    };

    try {
      chartRef.current = renderTelemetryChart(chartContainerRef.current, config);

      // Listen for drilldown events
      chartRef.current.on('drilldown', (data) => {
        isDrilldownInProgress.current = true;

        setStartDate(data.startDate);
        setEndDate(data.endDate);
        // Convert v1 interval format to v2 granularity format
        const newGranularity = data.interval === '1 hour' ? '1h' : '1d';
        setGranularity(newGranularity);

        // When drilling down to hourly view, ensure we have the full time range
        if (newGranularity === '1h') {
          setStartTime('00:00');
          setEndTime('23:59');
        }

        // Reset the flag after state updates have been processed
        setTimeout(() => {
          isDrilldownInProgress.current = false;
        }, 50);
      });
    } catch (error) {
      console.error('Failed to initialize chart:', error);
    }

    // Cleanup on unmount
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [device]);

  // Update chart when date range changes (but skip during drilldown events)
  useEffect(() => {
    if (chartRef.current && !isDrilldownInProgress.current) {
      chartRef.current.changePeriod(getStartDateTime(), getEndDateTime(), granularity);
    }
  }, [startDate, endDate, startTime, endTime, granularity]);

  const handleDateRangeChange = () => {
    if (chartRef.current) {
      chartRef.current.changePeriod(getStartDateTime(), getEndDateTime(), granularity);
    }
  };

  const setQuickRange = (days: number) => {
    const end = new Date();
    const startDate = new Date();
    startDate.setDate(end.getDate() - days);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(startDate.toISOString().split('T')[0]);

    // Set appropriate granularity based on range
    if (days <= 7) {
      setGranularity('1h');
    } else {
      setGranularity('1d');
    }
  };

  const handleManualRefresh = () => {
    if (!startDate || !endDate) {
      alert('Please select a date range first');
      return;
    }

    const end = new Date(endDate);
    const oneHourAgo = new Date();
    oneHourAgo.setHours(oneHourAgo.getHours() - 1);

    if (end >= oneHourAgo) {
      alert('Refresh is only needed for historical data (older than 1 hour). Recent data is automatically processed.');
      return;
    }

    if (confirm(`This will create a refresh job to process historical data from ${getStartDateTime()} to ${getEndDateTime()}. Continue?`)) {
      createRefreshJobMutation.mutate({ startDate: getStartDateTime(), endDate: getEndDateTime() });
    }
  };

  const isHistoricalData = () => {
    if (!endDate) return false;
    const end = new Date(endDate);
    const oneHourAgo = new Date();
    oneHourAgo.setHours(oneHourAgo.getHours() - 1);
    return end < oneHourAgo;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading device data</p>
        <button
          onClick={() => navigate('/management/devices')}
          className="mt-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Devices
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Devices
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {device.name || `Device ${device.id}`}
            </h1>
            <p className="text-sm text-gray-500">
              Gateway: {device.gatewayId} • Slave ID: {device.slaveId}
              {device.description && ` • ${device.description}`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <ChartBarIcon className="h-5 w-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Energy Data</span>
        </div>
      </div>

      {/* Device Information */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Device Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Device ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{device.id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Slave ID</dt>
              <dd className="mt-1 text-sm text-gray-900">{device.slaveId}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Gateway</dt>
              <dd className="mt-1 text-sm text-gray-900">{device.gatewayId}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Active
                </span>
              </dd>
            </div>
            {device.description && (
              <div className="md:col-span-2 lg:col-span-4">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900">{device.description}</dd>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Date Range Controls */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Energy Consumption Chart
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                View energy consumption data over time
              </p>
            </div>

            {/* Quick Range Buttons */}
            <div className="flex flex-wrap items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">Quick ranges:</span>
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setQuickRange(days)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {days} days
                </button>
              ))}
            </div>
          </div>

          {/* Date Picker Controls */}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-end space-y-4 sm:space-y-0 sm:space-x-4">
            <div>
              <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <div className="mt-1 relative">
                <input
                  type="date"
                  id="start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                />
                <CalendarIcon className="absolute right-3 top-2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <div className="mt-1 relative">
                <input
                  type="date"
                  id="end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                />
                <CalendarIcon className="absolute right-3 top-2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Time selectors - only show when hourly interval is selected */}
            {granularity === '1h' && (
              <>
                <div>
                  <label htmlFor="start-time" className="block text-sm font-medium text-gray-700">
                    Start Time
                  </label>
                  <div className="mt-1">
                    <input
                      type="time"
                      id="start-time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="end-time" className="block text-sm font-medium text-gray-700">
                    End Time
                  </label>
                  <div className="mt-1">
                    <input
                      type="time"
                      id="end-time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label htmlFor="interval" className="block text-sm font-medium text-gray-700">
                Interval
              </label>
              <div className="mt-1">
                <select
                  id="interval"
                  value={granularity}
                  onChange={(e) => setGranularity(e.target.value as '1d' | '1h')}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="1d">Daily</option>
                  <option value="1h">Hourly</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleDateRangeChange}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Update Chart
              </button>

              {isHistoricalData() && (
                <button
                  onClick={handleManualRefresh}
                  disabled={createRefreshJobMutation.isPending}
                  className="inline-flex items-center px-4 py-2 border border-orange-300 text-sm font-medium rounded-md shadow-sm text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                  title="Refresh historical aggregate data"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  {createRefreshJobMutation.isPending ? 'Creating...' : 'Refresh Data'}
                </button>
              )}
            </div>
          </div>

          {/* Historical Data Info Banner */}
          {isHistoricalData() && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Historical Data:</strong> You're viewing data older than 1 hour. If the chart appears empty or incomplete,
                    the system may automatically refresh the aggregate data in the background. You can also manually trigger a refresh
                    using the "Refresh Data" button above.
                  </p>
                  <div className="mt-2">
                    <button
                      onClick={() => navigate('/management/refresh-jobs')}
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      View refresh jobs status →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart Container */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div
            ref={chartContainerRef}
            className="w-full"
            style={{ height: '500px', minHeight: '400px' }}
          >
            {/* Chart will be rendered here by the SDK */}
          </div>
        </div>
      </div>
    </div>
  );
}
